import { supabase } from '../supabase';
import { Game, Recommendation, RecommendationAlgorithm, GameStats, StoreProduct } from '../../types';
import { POPULAR_RECOMMENDATION_CONFIG, RECOMMENDATION_CONFIG } from '../recommenders/index';
import { v4 as uuidv4 } from 'uuid';

/**
 * Serviço especializado para obter recomendações de jogos populares.
 */
export class PopularRecommendationService {

  constructor() {
    // Pode receber dependências no futuro, se necessário (ex: CacheService)
  }

  /**
   * Recupera recomendações de jogos populares baseados em avaliações e propriedade.
   * Lógica movida de RecommendationFacade.
   */
  public async getPopularRecommendations(
    userId: string,
    limit: number,
    excludeGameIds: string[] = []
  ): Promise<Recommendation[]> {
    try {
      const { data: ownedGames, error: ownedGamesError } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('user_id', userId)
        .eq('owned', true);

      if (ownedGamesError) {
        console.error('Erro ao buscar jogos do usuário:', ownedGamesError);
        return [];
      }

      const ownedGameIds = ownedGames.map(game => game.game_id);
      const allExcludeIds = [...new Set([...excludeGameIds, ...ownedGameIds])];
      
      console.log(`[PopularService] Total de ${allExcludeIds.length} jogos a serem excluídos da consulta de populares`);

      const gameFieldsSelect = 'id, name, year, ano_nacional, description, image_url, thumb_url, categories, min_players, max_players, play_time, min_age, complexity, average_rating, average_complexity, bgg_id, ludopedia_id, created_at, updated_at, mechanics, themes, artists, designers, slug, stats';

      const { data: ratedGames, error: ratedGamesError } = await supabase
        .from('games')
        .select(gameFieldsSelect)
        .gt('average_rating', POPULAR_RECOMMENDATION_CONFIG.query.minAverageRating)
        .order('average_rating', { ascending: false })
        .limit(POPULAR_RECOMMENDATION_CONFIG.query.initialLimit || 100);

      if (ratedGamesError) {
        console.error('[PopularService] Erro ao buscar jogos bem avaliados:', ratedGamesError);
        return [];
      }

      if (!ratedGames || ratedGames.length === 0) {
        console.log('[PopularService] Nenhum jogo bem avaliado encontrado após os filtros');
        return [];
      }
      
      const filteredRatedGames = ratedGames.filter(game => {
        let ratingCount = 0;
        try {
          if (typeof game.stats === 'object' && game.stats !== null) {
            if (typeof game.stats.rating_count === 'number') {
              ratingCount = game.stats.rating_count;
            } else if (game.stats['rating_count']) {
              ratingCount = Number(game.stats['rating_count']);
            } else if (typeof game.stats.rating_count === 'string') {
              ratingCount = Number(game.stats.rating_count);
            }
          }
        } catch (e) {
          console.log(`[PopularService] Erro ao acessar rating_count para o jogo ${game.id}:`, e);
        }
        const isExcluded = allExcludeIds.includes(game.id);
        const minRatingCount = 1;
        return ratingCount >= minRatingCount && !isExcluded;
      });
      
      if (filteredRatedGames.length === 0) {
        console.log('[PopularService] Nenhum jogo restante após aplicar exclusões e filtro de rating_count');
        return [];
      }
      
      const ratedGameIds = filteredRatedGames.map(game => game.id);
      const { data: ownershipData, error: ownershipError } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('owned', true)
        .in('game_id', ratedGameIds);

      if (ownershipError) {
        console.error('[PopularService] Erro ao buscar dados de propriedade:', ownershipError);
      }
      
      const ownershipCountMap = new Map<string, number>();
      if (ownershipData && ownershipData.length > 0) {
        ownershipData.forEach(item => {
          const gameId = item.game_id;
          const currentCount = ownershipCountMap.get(gameId) || 0;
          ownershipCountMap.set(gameId, currentCount + 1);
        });
      }
      
      const scoredGames = filteredRatedGames.map((game) => {
        const ownershipCount = ownershipCountMap.get(game.id) || 0;
        const weights = POPULAR_RECOMMENDATION_CONFIG.weights;
        const ratingCount = (game.stats as GameStats)?.rating_count || 0;
        
        const normalizedRating = (game.average_rating || 0) / 10;
        const normalizedRatingCount = Math.min(Math.log10(Math.max(1, ratingCount)) / 4.5, 1);
        const normalizedOwnership = Math.min(
          ownershipCount * (POPULAR_RECOMMENDATION_CONFIG.query.ownershipNormalizationFactor || 0.1), 
          1
        );
        
        const compositeScore = 
          (normalizedRating * weights.averageRating) +
          (normalizedRatingCount * weights.ratingCount) +
          (normalizedOwnership * weights.ownershipCount);
        
        return {
          ...(game as Game), 
          compositeScore
        };
      });

      const sortedRecommendationsData = scoredGames
        .sort((a, b) => b.compositeScore - a.compositeScore)
        .slice(0, limit);

      if (sortedRecommendationsData.length === 0) {
        console.log('[PopularService] Sem recomendações após pontuação composta, retornando fallback simples');
        return this.getFallbackRecommendations(userId, limit, allExcludeIds);
      }

      const recommendations: Recommendation[] = sortedRecommendationsData.map((scoredGame) => {
        const fullGame: Game = {
          id: scoredGame.id,
          name: scoredGame.name,
          year: scoredGame.year !== undefined ? scoredGame.year : null,
          ano_nacional: scoredGame.ano_nacional !== undefined ? scoredGame.ano_nacional : null,
          description: scoredGame.description !== undefined ? scoredGame.description : null,
          image_url: scoredGame.image_url,
          thumb_url: scoredGame.thumb_url !== undefined ? scoredGame.thumb_url : (scoredGame.image_url || null),
          categories: scoredGame.categories !== undefined ? scoredGame.categories : [],
          min_players: scoredGame.min_players !== undefined ? scoredGame.min_players : null,
          max_players: scoredGame.max_players !== undefined ? scoredGame.max_players : null,
          play_time: scoredGame.play_time !== undefined ? scoredGame.play_time : null,
          min_age: scoredGame.min_age !== undefined ? scoredGame.min_age : null,
          complexity: scoredGame.complexity !== undefined ? scoredGame.complexity : null,
          average_rating: scoredGame.average_rating !== undefined ? scoredGame.average_rating : null,
          average_complexity: scoredGame.average_complexity !== undefined ? scoredGame.average_complexity : null,
          bgg_id: scoredGame.bgg_id !== undefined ? scoredGame.bgg_id : null,
          ludopedia_id: scoredGame.ludopedia_id !== undefined ? scoredGame.ludopedia_id : null,
          created_at: scoredGame.created_at || new Date().toISOString(),
          updated_at: scoredGame.updated_at || new Date().toISOString(),
          mechanics: scoredGame.mechanics !== undefined ? scoredGame.mechanics : [],
          themes: scoredGame.themes !== undefined ? scoredGame.themes : [],
          artists: scoredGame.artists !== undefined ? scoredGame.artists : [],
          designers: scoredGame.designers !== undefined ? scoredGame.designers : [],
          slug: scoredGame.slug !== undefined ? scoredGame.slug : null,
          stats: scoredGame.stats as GameStats | undefined
        };
        const ratingDisplay = (typeof fullGame.average_rating === 'number') 
                              ? fullGame.average_rating.toFixed(1) 
                              : 'N/A';
        return {
          id: uuidv4(),
          user_id: userId,
          game_id: fullGame.id,
          score: scoredGame.compositeScore,
          reason: `Jogo popular com alta avaliação (${ratingDisplay}/5) na comunidade.`,
          algorithm: RecommendationAlgorithm.POPULAR,
          viewed: false,
          liked: null,
          game: fullGame
        };
      });
      return recommendations;
    } catch (error) {
      console.error('[PopularService] Erro ao buscar recomendações populares:', error);
      return [];
    }
  }

  /**
   * Método de fallback para garantir que sempre retornemos recomendações populares.
   * Lógica movida de RecommendationFacade.
   */
  public async getFallbackRecommendations(
    userId: string, 
    limit: number, 
    excludeGameIds: string[] = []
  ): Promise<Recommendation[]> {
    console.log(`[PopularService] Executando rotina de fallback para obter recomendações populares para user ${userId}`);
    
    const gameFieldsSelect = 'id, name, year, ano_nacional, description, image_url, thumb_url, categories, min_players, max_players, play_time, min_age, complexity, average_rating, average_complexity, bgg_id, ludopedia_id, created_at, updated_at, mechanics, themes, artists, designers, slug, stats';

    try {
      const { data: fallbackGamesRaw, error: fallbackError } = await supabase
        .from('games')
        .select(gameFieldsSelect)
        .gt('average_rating', 0)
        .order('average_rating', { ascending: false })
        .limit(200);
        
      let gamesToProcess: Partial<Game>[] = [];

      if (fallbackError || !fallbackGamesRaw || fallbackGamesRaw.length === 0) {
        console.error('[PopularService] Erro ao buscar fallback inicial ou nenhum jogo encontrado, tentando segunda busca mais ampla:', fallbackError);
        const { data: anyGamesRaw } = await supabase
          .from('games')
          .select(gameFieldsSelect)
          .limit(50);
        if (anyGamesRaw && anyGamesRaw.length > 0) {
          gamesToProcess = anyGamesRaw as Partial<Game>[];
        }
      } else {
        gamesToProcess = fallbackGamesRaw as Partial<Game>[];
      }
          
      if (gamesToProcess.length === 0) {
        console.log('[PopularService] Nenhum jogo encontrado para fallback após todas as tentativas.');
        return [];
      }
        
      const filteredGames = gamesToProcess.filter(game => game.id && !excludeGameIds.includes(game.id));
      
      let finalGamesForMapping = filteredGames;
      if (filteredGames.length === 0 && gamesToProcess.length > 0) {
        console.log('[PopularService] Todos os jogos do fallback estão na lista de exclusão, usando subset aleatório dos originais.');
        finalGamesForMapping = gamesToProcess.filter(g => g.id).sort(() => 0.5 - Math.random());
      }

      if (finalGamesForMapping.length === 0) {
        console.log('[PopularService] Nenhum jogo para mapear após todas as filtragens de fallback.');
        return [];
      }
        
      return finalGamesForMapping.slice(0, limit).map(game => {
        const fullGame: Game = {
          id: game.id || uuidv4(),
          name: game.name || 'Jogo Desconhecido',
          year: game.year !== undefined ? game.year : null,
          ano_nacional: game.ano_nacional !== undefined ? game.ano_nacional : null,
          description: game.description !== undefined ? game.description : null,
          image_url: game.image_url !== undefined ? game.image_url : null,
          thumb_url: game.thumb_url !== undefined ? game.thumb_url : (game.image_url || null),
          categories: game.categories !== undefined ? game.categories : [],
          min_players: game.min_players !== undefined ? game.min_players : null,
          max_players: game.max_players !== undefined ? game.max_players : null,
          play_time: game.play_time !== undefined ? game.play_time : null,
          min_age: game.min_age !== undefined ? game.min_age : null,
          complexity: game.complexity !== undefined ? game.complexity : null,
          average_rating: game.average_rating !== undefined ? game.average_rating : null,
          average_complexity: game.average_complexity !== undefined ? game.average_complexity : null,
          bgg_id: game.bgg_id !== undefined ? game.bgg_id : null,
          ludopedia_id: game.ludopedia_id !== undefined ? game.ludopedia_id : null,
          created_at: game.created_at || new Date().toISOString(),
          updated_at: game.updated_at || new Date().toISOString(),
          mechanics: game.mechanics !== undefined ? game.mechanics : [],
          themes: game.themes !== undefined ? game.themes : [],
          artists: game.artists !== undefined ? game.artists : [],
          designers: game.designers !== undefined ? game.designers : [],
          slug: game.slug !== undefined ? game.slug : null,
          stats: game.stats as GameStats | undefined,
        };
        const ratingDisplay = (typeof fullGame.average_rating === 'number') 
                              ? fullGame.average_rating.toFixed(1) 
                              : 'N/A';
        return {
          id: uuidv4(),
          user_id: userId,
          game_id: fullGame.id,
          score: typeof fullGame.average_rating === 'number' ? fullGame.average_rating : 5,
          reason: `Jogo popular com boa avaliação (${ratingDisplay}/5)`,
          algorithm: RecommendationAlgorithm.POPULAR,
          viewed: false,
          liked: null,
          game: fullGame
        };
      });
    } catch (error) {
      console.error('[PopularService] Erro no fallback de recomendações:', error);
      return [];
    }
  }
} 